import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { META } from "./meta";
import { BASE_URL } from "@/core/constants/business";
import BlogSearch from "./BlogSearch";
import BlogGrid from "./BlogGrid";
import Hero from "./Hero";
import blogDataDC from "@/core/Blogs/data/DC/database_content_updated-dc.json";

interface BlogPost {
  pk: string;
  sk: string;
  title: string;
  metaDescription: string;
  introParagraph?: string;
  middleParagraph?: string;
  conclusionParagraph?: string;
  subject?: string;
  city?: string;
  state?: string;
}

// Filter and validate the blog data
const getValidBlogs = (): BlogPost[] => {
  try {
    // Check if data is array
    if (!Array.isArray(blogDataDC)) {
      console.error("Blog data is not an array:", typeof blogDataDC);
      return [];
    }

    const validBlogs = blogDataDC.filter((blog: any) => {
      return (
        blog &&
        blog.sk &&
        blog.title &&
        blog.metaDescription &&
        typeof blog.sk === "string" &&
        typeof blog.title === "string"
      );
    }) as BlogPost[];

    return validBlogs;
  } catch (error) {
    console.error("Error loading blog data:", error);
    return [];
  }
};

const AllBlogs: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [filteredBlogs, setFilteredBlogs] = useState<BlogPost[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const blogsPerPage = 12;

  useEffect(() => {
    try {
      const validBlogs = getValidBlogs();
      setBlogs(validBlogs);
      setFilteredBlogs(validBlogs);
    } catch (error) {
      console.error("Failed to load blogs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!blogs.length) return;

    const filtered = blogs.filter((blog) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        blog.title?.toLowerCase().includes(searchLower) ||
        blog.metaDescription?.toLowerCase().includes(searchLower) ||
        blog.subject?.toLowerCase().includes(searchLower) ||
        blog.city?.toLowerCase().includes(searchLower)
      );
    });

    setFilteredBlogs(filtered);
    setCurrentPage(1);
  }, [searchTerm, blogs]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Calculate pagination variables
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-cannabis-green-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg">Loading cannabis articles...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{META.title}</title>
        <meta name="description" content={META.meta_description} />
        <meta name="keywords" content={META.keywords} />
        <link rel="canonical" href={`${BASE_URL}${META.canonicalUrl}`} />
      </Helmet>

      {/* Hero Section */}
      <Hero />

      <div className="min-h-screen bg-white">
        {/* Search Section */}
        <BlogSearch
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          totalBlogs={blogs.length}
          filteredCount={filteredBlogs.length}
        />

        {/* Blog Grid Section */}
        <section className="pb-16">
          <div className="w-11/12 max-w-6xl mx-auto">
            {blogs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm p-8">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Our Blog Articles Are Coming Soon
                </h3>
                <p className="text-gray-600">
                  We're working on bringing you informative cannabis content.
                  Check back soon for articles about products, education, and
                  industry news.
                </p>
              </div>
            ) : (
              <BlogGrid
                currentBlogs={currentBlogs}
                totalBlogs={filteredBlogs.length}
                blogsPerPage={blogsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                searchTerm={searchTerm}
              />
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default AllBlogs;
